import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import AgGrid from "../components/grid/AgGrid";
import { M3Badge } from "../components/m3";

import {
  defaultColDef,
  leftAlignColDef,
  rightAlignColDef,
  centerAlignColDef,
} from "../components/grid/columnDefinitions/columnDefinitions";

const SCOPE_TONE_MAP = {
  Total: "success",
  "Scope 1": "error",
  "Scope 2": "warning",
  "Scope 3": "info",
  Avoided: "primary",
  Biogenic: "tertiary",
};

const ScopeBadgeRenderer = (params) => {
  const scope = params.value;
  const isPinnedTotal = params.data?.isPinnedTotal;
  const tone = isPinnedTotal ? "success" : (SCOPE_TONE_MAP[scope] || "neutral");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <M3Badge tone={tone} size="md">
        {scope}
      </M3Badge>
    </div>
  );
};

const formatPollutantValue = (val) => {
  if (!val || val === 0) return "0.00";
  if (val >= 0.01) {
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  // For tiny values like 0.0011 or 0.0007: show precise decimals up to 4 digits
  return Number(val.toFixed(4)).toString();
};

const EmissionsCellRenderer = ({ value, data, isDark, unit = "kg" }) => {
  const pollutants = data?.pollutants || [];
  const isPinnedTotal = data?.isPinnedTotal;

  const formattedGwp = value
    ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "14px",
        width: "100%",
        padding: "6px 0",
        fontFamily: "'Poppins', sans-serif",
        boxSizing: "border-box",
      }}
    >
      {/* Primary Emissions Value Pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isPinnedTotal ? "5px 14px" : "4px 12px",
          borderRadius: "8px",
          fontSize: isPinnedTotal ? "12.5px" : "12px",
          fontWeight: 700,
          backgroundColor: isDark
            ? (isPinnedTotal ? "rgba(101, 157, 51, 0.35)" : "rgba(101, 157, 51, 0.22)")
            : (isPinnedTotal ? "rgba(101, 157, 51, 0.25)" : "rgba(101, 157, 51, 0.15)"),
          border: `1.5px solid ${isDark ? "rgba(101, 157, 51, 0.8)" : "rgba(101, 157, 51, 0.55)"}`,
          color: isDark ? "#4ade80" : "#2e7d32",
          lineHeight: "1.2",
          boxShadow: isPinnedTotal ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 700, fontFamily: "'Poppins', sans-serif" }}>
          {formattedGwp} {unit} CO₂e
        </span>
      </div>

      {/* Vertical Pollutant Breakdown Chips with EQUAL UNIFORM WIDTH */}
      {pollutants.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "flex-end",
            flexShrink: 0,
          }}
        >
          {pollutants.map((p) => {
            const valStr = formatPollutantValue(p.value);

            return (
              <div
                key={p.key}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "140px",
                  boxSizing: "border-box",
                  padding: "3px 10px",
                  borderRadius: "6px",
                  fontSize: isPinnedTotal ? "11.5px" : "11px",
                  fontWeight: isPinnedTotal ? 700 : 600,
                  backgroundColor: isDark
                    ? `${p.color}${isPinnedTotal ? "35" : "20"}`
                    : `${p.color}${isPinnedTotal ? "22" : "12"}`,
                  border: `${isPinnedTotal ? "1.5px" : "1px"} solid ${p.color}${isPinnedTotal ? "70" : "40"}`,
                  color: isDark ? "#ffffff" : "#1e293b",
                  lineHeight: "1.2",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ color: p.color, fontWeight: 700, fontSize: "11px" }}>{p.label}:</span>
                <span style={{ fontWeight: isPinnedTotal ? 700 : 600, fontFamily: "'Poppins', sans-serif" }}>
                  {valStr} {p.unit}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ScopeSummaryGrid = ({ rowData = [], isDark = false, unit = "kg" }) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);

  const pinnedBottomRowData = useMemo(() => {
    if (!rowData || rowData.length === 0) return [];

    let totalGwp = 0;
    const pollutantSums = {
      co2: 0,
      ch4: 0,
      n2o: 0,
      nox: 0,
      pm25: 0,
      pm10: 0,
      black_carbon: 0
    };

    rowData.forEach((row) => {
      totalGwp += Number(row.gwp) || 0;
      (row.pollutants || []).forEach((p) => {
        if (pollutantSums[p.key] !== undefined) {
          pollutantSums[p.key] += Number(p.value) || 0;
        }
      });
    });

    const grandPollutants = [
      { key: "co2", label: "CO₂", unit, color: "#3b82f6" },
      { key: "ch4", label: "CH₄", unit, color: "#eab308" },
      { key: "n2o", label: "N₂O", unit, color: "#a855f7" },
      { key: "nox", label: "NOx", unit, color: "#ef4444" },
      { key: "pm25", label: "PM2.5", unit, color: "#f97316" },
      { key: "pm10", label: "PM10", unit, color: "#06b6d4" },
      { key: "black_carbon", label: "Black Carbon", unit, color: "#64748b" }
    ]
      .filter((p) => pollutantSums[p.key] > 0)
      .map((p) => ({
        ...p,
        value: pollutantSums[p.key]
      }));

    return [
      {
        id: "grand-total-pinned-row",
        scope: "Total",
        category: "All Scopes Summary",
        gwp: totalGwp,
        pollutants: grandPollutants,
        isPinnedTotal: true
      }
    ];
  }, [rowData, unit]);

  const columnDefs = useMemo(
    () => [
      {
        field: "scope",
        headerName: t("Scope"),
        ...defaultColDef,
        ...centerAlignColDef,
        cellStyle: { ...centerAlignColDef.cellStyle, display: "flex", alignItems: "center", justifyContent: "center" },
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: ScopeBadgeRenderer,
        resizable: true,
        suppressMovable: false,
        sortable: false
      },
      {
        field: "category",
        headerName: t("Category"),
        ...defaultColDef,
        ...leftAlignColDef,
        cellStyle: (params) => ({
          ...leftAlignColDef.cellStyle,
          paddingLeft: "8px",
          display: "flex",
          alignItems: "center",
          fontWeight: params.data?.isPinnedTotal ? 700 : 500
        }),
        width: 250,
        minWidth: 170,
        resizable: true,
        suppressMovable: false,
        sortable: false
      },
      {
        field: "gwp",
        headerName: t(`Emissions (${unit} CO₂e)`),
        ...defaultColDef,
        ...rightAlignColDef,
        flex: 1,
        minWidth: 420,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          overflow: "visible",
        },
        cellRenderer: (params) => <EmissionsCellRenderer {...params} isDark={isDark} unit={unit} />,
        resizable: true,
        suppressMovable: false,
        sortable: false
      }
    ],
    [t, unit, isDark]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      gridRef.current?.api?.resetRowHeights?.();
    }, 60);
    return () => clearTimeout(timer);
  }, [rowData, pinnedBottomRowData]);

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDefs}
      rowData={rowData}
      pinnedBottomRowData={pinnedBottomRowData}
      getRowId={(params) => params.data.id}
      domLayout="autoHeight"
      suppressRowClickSelection
      headerHeight={48}
      getRowHeight={(params) => {
        const pCount = params.data?.pollutants?.length || 0;
        if (pCount > 0) {
          // Each pollutant chip is 24px + 4px gap = 28px.
          // Generous top/bottom cell padding (24px) ensures no chip is ever clipped.
          return Math.max(54, pCount * 28 + 24);
        }
        return params.node.rowPinned === "bottom" ? 56 : 48;
      }}
      sx={{
        "& .ag-cell": {
          overflow: "visible !important",
        },
        "& .ag-header-cell:not(:last-child)::after": {
          content: '""',
          position: "absolute",
          right: 0,
          top: "25%",
          height: "50%",
          width: "1px",
          backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
          display: "block !important"
        },
        "& .ag-row-first": {
          borderTop: "none !important",
        },
        "& .ag-floating-bottom": {
          borderTop: isDark ? "2px solid rgba(255,255,255,0.15) !important" : "2px solid rgba(0,0,0,0.1) !important",
          fontWeight: "bold"
        },
        "& .ag-floating-bottom .ag-row": {
          backgroundColor: isDark ? "#202024 !important" : "#f1f5f9 !important",
        }
      }}
    />
  );
};

export default ScopeSummaryGrid;

